/**
 * globalChrome — the builder's owner of the STORE-WIDE header / footer documents.
 *
 * THE PROBLEM THIS SOLVES (founder call 2026-08-16). Until now every page document carried its own copy
 * of the header / announcement bar / footer, and the builder edited exactly one document at a time. So a
 * header edit made on the home reached the home and nothing else, the product page kept whatever header
 * it had been seeded with, and the two drifted apart with no way to reconcile them from the editor. The
 * storefront made it worse by SKIPPING the global chrome whenever a page carried its own.
 *
 * THE MODEL NOW. The chrome lives in two documents per store+locale — `template='header'` and
 * `template='footer'`, handle `'default'` — which the storefront composes around every page. The builder
 * keeps editing ONE flat section list (the rail, the canvas, the inspector, undo/redo and the dnd rules
 * are untouched), but that list is COMPOSED on load and SPLIT on save:
 *
 *     header document   ─┐
 *     page document     ─┼─→ one flat `order` + `sectionsById` the editor works on
 *     footer document   ─┘
 *
 * Zone membership comes from the section TYPE (`zoneOfType`, shared with the storefront), which is how
 * the rail has always grouped its HEADER / TEMPLATE / FOOTER rows — so there is no provenance flag to
 * keep in sync and no way for the two sides to disagree.
 *
 * CONSEQUENCE, BY CONSTRUCTION: editing the header from ANY page edits the one document every page
 * shows. Switching pages mid-session re-composes the SAME live chrome (the per-page cache holds body
 * sections only), so a header edit is visible on the next page without a round-trip.
 *
 * This module owns the chrome's own optimistic-lock versions and publish state — one `@Version` per
 * chrome document, independent of the page's, because they are separate rows on the backend.
 */
import {
  chromeDocumentOf,
  splitChromeSections,
  type PageDocument,
  type SectionInstance,
} from '@aquilixshop/ui-react';
import { builderApi, type BuilderRoute } from '../lib/builderApi';
import { HttpError, NetworkError } from '../lib/errors';

/** The two global chrome documents, by template key. */
export type ChromeTemplate = 'header' | 'footer';
export const CHROME_TEMPLATES: readonly ChromeTemplate[] = ['header', 'footer'];

/**
 * The DB row key a chrome document lives under. `undefined` = the STORE's own language, resolved
 * server-side exactly like a page route without `locale` (0050 lot 0). NEVER the collapsed fr/ar/en
 * text locale: an English store whose chrome was keyed `fr` published its logo into rows the
 * storefront never serves (novendra, 2026-09-13).
 */
export type ChromeLocale = string | undefined;

/** One loaded chrome document: its sections plus the row state needed to save and publish it. */
interface ChromeDoc {
  sections: SectionInstance[];
  /** Optimistic-lock token. 0 = the row does not exist yet; the first PUT creates it. */
  version: number;
  publishedAt: string | null;
  hasUnpublishedChanges: boolean;
  /** Serialized snapshot of what the server holds, so we only PUT chrome that actually changed. */
  savedMarker: string;
}

interface LoadedChrome {
  key: string;
  handle: string;
  locale: ChromeLocale;
  header: ChromeDoc;
  footer: ChromeDoc;
}

/**
 * The session's live chrome. Store-global, NOT per page — that is the whole point: every page composes
 * from this one object, so there is exactly one header in the editor no matter which page is open.
 */
let loaded: LoadedChrome | null = null;
/** De-dupes concurrent loads (page switch + prefetch can both ask at once). */
let inFlight: Promise<LoadedChrome> | null = null;
/** Set by a real seller edit (see `chromeHasPendingEdit`); cleared once the chrome reaches the server. */
let editedInSession = false;

/**
 * The handle a page's chrome lives under. Normally `'default'` — the ONE chrome the whole store shares;
 * a CMS page's own handle (its slug) must never fork the chrome. The exception is a THEME WORKSPACE
 * (spec 0032, "one draft per theme"): its documents are staged under the workspace handle, so its chrome
 * is staged there too and only reaches the live store when the theme is published.
 */
export function chromeHandleFor(handle: string, isThemeWorkspace: boolean): string {
  return isThemeWorkspace ? handle : 'default';
}

export function chromeRoute(
  template: ChromeTemplate,
  handle: string,
  locale: ChromeLocale,
): BuilderRoute {
  return { template, handle, locale };
}

function keyOf(handle: string, locale: ChromeLocale): string {
  return `${handle}|${locale ?? ''}`;
}

function markerOf(sections: SectionInstance[]): string {
  return JSON.stringify(sections);
}

/** A 404 / offline GET = the store has no such chrome row yet → start from the theme seed at version 0. */
function isMissing(e: unknown): boolean {
  return e instanceof NetworkError || (e instanceof HttpError && e.status === 404);
}

async function fetchChromeDoc(
  template: ChromeTemplate,
  handle: string,
  locale: ChromeLocale,
  // ASYNC-tolerant, and only ever called on the missing-row path below. The seed comes from the THEME,
  // and a theme's documents are now a dynamic import (ui-react themes/registry.ts) — so the chunk is
  // fetched by the store that has no chrome row yet, and never by the far more common store that has one.
  seed: () => SectionInstance[] | Promise<SectionInstance[]>,
): Promise<ChromeDoc> {
  try {
    const res = await builderApi.getDraft(chromeRoute(template, handle, locale));
    // Only the matching zone is kept: a legacy chrome row that somehow holds body sections (or a footer
    // section inside the header row) must not leak page content into the chrome region.
    const zones = splitChromeSections(res.document?.sections);
    const sections = template === 'header' ? zones.header : zones.footer;
    return {
      sections,
      version: res.version,
      publishedAt: res.publishedAt,
      hasUnpublishedChanges: res.hasUnpublishedChanges,
      savedMarker: markerOf(sections),
    };
  } catch (e) {
    if (!isMissing(e)) throw e;
    const sections = await seed();
    return {
      sections,
      version: 0,
      publishedAt: null,
      hasUnpublishedChanges: false,
      // NOT the seed's marker: an unsaved seed must be PUT on the first save so the row gets created.
      savedMarker: '<unsaved>',
    };
  }
}

/**
 * Load (once per handle+locale) the store's global chrome. `seedHeader` / `seedFooter` supply the
 * active theme's chrome for a store that has no chrome row yet, so the builder always opens on a
 * complete page instead of a bare body.
 */
export async function ensureChromeLoaded(
  handle: string,
  locale: ChromeLocale,
  seedHeader: () => SectionInstance[] | Promise<SectionInstance[]>,
  seedFooter: () => SectionInstance[] | Promise<SectionInstance[]>,
): Promise<LoadedChrome> {
  const key = keyOf(handle, locale);
  if (loaded && loaded.key === key) return loaded;
  if (inFlight) {
    const pending = await inFlight;
    if (pending.key === key) return pending;
  }
  inFlight = (async () => {
    const [header, footer] = await Promise.all([
      fetchChromeDoc('header', handle, locale, seedHeader),
      fetchChromeDoc('footer', handle, locale, seedFooter),
    ]);
    const next: LoadedChrome = { key, handle, locale, header, footer };
    loaded = next;
    return next;
  })();
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

/** The live chrome sections to compose around a page body (empty when nothing is loaded yet). */
export function chromeSections(): { header: SectionInstance[]; footer: SectionInstance[] } {
  return { header: loaded?.header.sections ?? [], footer: loaded?.footer.sections ?? [] };
}

export function isChromeLoaded(): boolean {
  return loaded != null;
}

/**
 * Whether the live chrome is the one this route needs. Guards the builder's SEAMLESS (cache-restore)
 * page switch, which is synchronous and therefore cannot await a chrome fetch: crossing into or out of
 * a theme workspace changes the chrome handle, so that switch must take the fetch path instead.
 */
export function chromeMatches(handle: string, locale: ChromeLocale): boolean {
  return loaded != null && loaded.key === keyOf(handle, locale);
}

/**
 * Adopt the chrome the editor currently holds. Called on every save/split so the in-memory chrome — the
 * one every OTHER page will compose from — reflects the edit immediately, before any network round-trip.
 */
export function adoptEditedChrome(header: SectionInstance[], footer: SectionInstance[]): void {
  if (!loaded) return;
  if (
    markerOf(header) !== markerOf(loaded.header.sections) ||
    markerOf(footer) !== markerOf(loaded.footer.sections)
  ) {
    editedInSession = true;
  }
  loaded.header.sections = header;
  loaded.footer.sections = footer;
}

/**
 * Whether the seller has actually EDITED the chrome this session — as opposed to a chrome that merely
 * needs writing because the store had no row yet and it was seeded from the theme.
 *
 * The distinction matters on a page switch: a real edit must follow the seller onto the next page and be
 * flushed from there (the chrome is store-global, `dirty` is per page), while merely OPENING a page must
 * stay clean and never autosave on its own — the seeded chrome is created by the first genuine save.
 */
export function chromeHasPendingEdit(): boolean {
  return editedInSession && chromeIsDirty();
}

/**
 * Adopt the chrome sections as HYDRATED by the editor (defaults filled, lazy migrations applied).
 *
 * Hydration is not a seller edit, so for a row that EXISTS on the server we re-baseline the saved marker
 * with it — otherwise merely opening a page would leave the chrome permanently "changed" and queue a
 * write nobody asked for. A row that does not exist yet keeps its `<unsaved>` marker so the first save
 * actually creates it.
 */
export function adoptHydratedChrome(header: SectionInstance[], footer: SectionInstance[]): void {
  if (!loaded) return;
  loaded.header.sections = header;
  loaded.footer.sections = footer;
  if (loaded.header.version > 0) loaded.header.savedMarker = markerOf(header);
  if (loaded.footer.version > 0) loaded.footer.savedMarker = markerOf(footer);
}

/**
 * Seed the session's chrome with NO network — the DEV theme-preset routes (`?preset=electronics`) and
 * the offline default-seed path, which must open a complete page without a store behind them.
 */
export function seedOfflineChrome(
  handle: string,
  locale: ChromeLocale,
  header: SectionInstance[],
  footer: SectionInstance[],
): void {
  loaded = {
    key: keyOf(handle, locale),
    handle,
    locale,
    header: {
      sections: header,
      version: 0,
      publishedAt: null,
      hasUnpublishedChanges: false,
      savedMarker: '<unsaved>',
    },
    footer: {
      sections: footer,
      version: 0,
      publishedAt: null,
      hasUnpublishedChanges: false,
      savedMarker: '<unsaved>',
    },
  };
}

/** True when either chrome document differs from what the server last acknowledged. */
export function chromeIsDirty(): boolean {
  if (!loaded) return false;
  return (
    markerOf(loaded.header.sections) !== loaded.header.savedMarker ||
    markerOf(loaded.footer.sections) !== loaded.footer.savedMarker
  );
}

export function chromeHasUnpublishedChanges(): boolean {
  return !!loaded && (loaded.header.hasUnpublishedChanges || loaded.footer.hasUnpublishedChanges);
}

/**
 * PUT the chrome documents that changed, swapping each row's `@Version` in place (same contract as the
 * page autosave: no re-fetch). Throws on the first failure so the caller can classify it exactly like a
 * page save error — a chrome save that 409s must park the editor in `conflict`, not fail silently.
 *
 * `source` supplies schemaVersion + locale for the document envelope.
 */
export async function saveChromeDrafts(source: PageDocument): Promise<void> {
  if (!loaded) return;
  for (const template of CHROME_TEMPLATES) {
    const doc = loaded[template];
    const marker = markerOf(doc.sections);
    if (marker === doc.savedMarker) continue;
    const payload = chromeDocumentOf(source, template, loaded.handle, doc.sections);
    const res = await builderApi.saveDraft(
      chromeRoute(template, loaded.handle, loaded.locale),
      payload,
      doc.version,
    );
    doc.version = res.version;
    doc.savedMarker = marker;
    doc.hasUnpublishedChanges = true;
  }
  editedInSession = false;
}

/** One chrome row a keepalive flush must PUT, with everything needed to adopt its answer. */
export interface ChromeKeepalivePayload {
  /** The loaded chrome this payload was built from — see {@link adoptChromeSavedVersion}. */
  key: string;
  template: ChromeTemplate;
  route: BuilderRoute;
  document: PageDocument;
  version: number;
  /** Marker of the sections being sent, so the response can set `savedMarker` to what the server holds. */
  marker: string;
}

/**
 * The chrome writes a `keepalive` tab-close flush must dispatch — one per CHANGED chrome document. Same
 * fire-and-forget contract as the page's keepalive PUT: no response is read, so no version is swapped;
 * a 409 in that window is reconciled on the next open through the normal conflict path.
 */
export function keepaliveChromePayloads(source: PageDocument): ChromeKeepalivePayload[] {
  if (!loaded) return [];
  const out: ChromeKeepalivePayload[] = [];
  for (const template of CHROME_TEMPLATES) {
    const doc = loaded[template];
    const marker = markerOf(doc.sections);
    if (marker === doc.savedMarker) continue;
    out.push({
      key: loaded.key,
      template,
      route: chromeRoute(template, loaded.handle, loaded.locale),
      document: chromeDocumentOf(source, template, loaded.handle, doc.sections),
      version: doc.version,
      marker,
    });
  }
  return out;
}

/**
 * Adopt the `@Version` a keepalive chrome PUT came back with.
 *
 * A keepalive PUT is a REAL write — the row moves to a new `@Version` whether or not anyone reads the
 * answer. When nobody did, this module kept the OLD token and the next ordinary chrome save was a stale
 * write the backend answered 409 to: the editor conflicting with its own last request (founder,
 * 2026-09-19). The page half of this lives in `autosave.keepaliveFlush`.
 *
 * `key` is the chrome that was on the wire; a different one is loaded now (another locale, another
 * store), so its version is not ours to write. Unknown → dropped, and the next open re-reads it.
 */
export function adoptChromeSavedVersion(
  key: string,
  template: ChromeTemplate,
  version: number,
  marker: string,
): void {
  if (!loaded || loaded.key !== key) return;
  const doc = loaded[template];
  doc.version = version;
  // `savedMarker` is what the SERVER now holds, not what the editor holds: an edit made while the PUT
  // was in flight must stay pending, and it does, because its marker no longer matches this one.
  doc.savedMarker = marker;
  doc.hasUnpublishedChanges = true;
}

/**
 * Publish the chrome documents that have unpublished changes. Publishing ANY page publishes the store's
 * chrome with it: the chrome is not the page's, but a visitor landing on that page sees both, and a
 * published body wrapped in a stale header is precisely the mismatch this whole change removes.
 */
export async function publishChromeDrafts(): Promise<void> {
  if (!loaded) return;
  for (const template of CHROME_TEMPLATES) {
    const doc = loaded[template];
    if (!doc.hasUnpublishedChanges) continue;
    await builderApi.publish(chromeRoute(template, loaded.handle, loaded.locale));
    doc.publishedAt = new Date().toISOString();
    doc.hasUnpublishedChanges = false;
  }
}

/**
 * Re-read the chrome rows and adopt their versions — the chrome half of "keep my changes" after a 409.
 * Keeps the EDITOR's sections (the seller chose to overwrite) and only refreshes the lock tokens.
 */
export async function reconcileChromeVersions(): Promise<void> {
  if (!loaded) return;
  for (const template of CHROME_TEMPLATES) {
    const doc = loaded[template];
    try {
      const latest = await builderApi.getDraft(chromeRoute(template, loaded.handle, loaded.locale));
      doc.version = latest.version;
      doc.publishedAt = latest.publishedAt;
      doc.hasUnpublishedChanges = latest.hasUnpublishedChanges;
      doc.savedMarker = '<unsaved>'; // force a re-PUT of the in-memory chrome
    } catch (e) {
      if (!isMissing(e)) throw e;
      doc.version = 0;
      doc.savedMarker = '<unsaved>';
    }
  }
}

/** Drop the session's chrome (route to another store, theme workspace switch, test reset). */
export function resetGlobalChrome(): void {
  loaded = null;
  inFlight = null;
  editedInSession = false;
}
